import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

// Splash now leads with the glowing teal enso brand mark on a deep-navy
// stage so the logo's built-in halo blends into the background instead of
// sitting inside a white "trapped" card.  No surrounding chrome — the
// image is the splash, the page just orbits it.
// The "-transparent" variant has its dark navy background made transparent
// at build time (see /app/frontend/public/brand/) so the bloom dissolves
// cleanly into any dark page bg without a visible rectangle.
const ZENSCROLL_LOGO = '/brand/zenscroll-logo-glow-transparent.png';

export default function Splash() {
  const navigate = useNavigate();

  // Hide Emergent badge on splash page
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.display = 'none';
    }
    return () => {
      if (badge) {
        badge.style.display = 'flex';
      }
    };
  }, []);

  const handleSignIn = () => {
    navigate('/auth');
  };

  const handleSignUp = () => {
    navigate('/auth?mode=signup');
  };

  return (
    <div
      data-testid="splash-screen"
      className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#06070A]"
    >
      {/* Soft teal aurora glow behind the logo — extends the brand-mark's
          own halo so the page feels like one continuous bloom instead of a
          stamped card on a flat color.  Kept subtle so it doesn't form a
          visible oval halo of its own. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[680px] h-[680px] rounded-full"
        style={{
          background:
            'radial-gradient(closest-side, rgba(94,234,212,0.10), rgba(20,184,166,0.03) 55%, transparent 75%)',
          filter: 'blur(14px)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 flex flex-col items-center w-full max-w-[420px]"
      >
        {/* Logo — flush, no card, no padding box. The transparent PNG
            has its dark navy bg already removed at build time, so the
            glow simply blooms into the page without a visible rectangle. */}
        <img
          data-testid="splash-logo"
          src={ZENSCROLL_LOGO}
          alt="ZenScroll — Your Feed Reinvented by AI"
          className="w-[320px] sm:w-[380px] h-auto select-none"
          draggable={false}
        />

        {/* CTAs — sit directly under the wordmark with a tight rhythm so the
            page feels like one composition, not two stacked sections. */}
        <div className="-mt-2 w-full px-6 flex flex-col gap-3">
          <button
            data-testid="splash-signup-btn"
            onClick={handleSignUp}
            className="w-full py-3 px-4 rounded-full bg-[#0D9488] text-white font-medium text-sm hover:bg-[#0F766E] transition-colors shadow-[0_0_28px_rgba(13,148,136,0.35)]"
          >
            Sign Up
          </button>
          <button
            data-testid="splash-signin-btn"
            onClick={handleSignIn}
            className="w-full py-3 px-4 rounded-full bg-transparent text-white/80 font-medium text-sm border border-white/15 hover:bg-white/5 hover:text-white transition-colors"
          >
            Sign In
          </button>
        </div>
      </motion.div>
    </div>
  );
}
